import React from 'react'
import { Checkbox, Dropdown as UnstyledDropdown, Form, Input } from 'semantic-ui-react'
import styled from 'styled-components'
import classNames from 'classnames'
import * as R from 'ramda'

import { SliderQuestionInput } from 'components/SliderQuestionInput'
import { MultipleChoiceQuestionInput } from 'components/MultipleChoiceQuestionInput'
import { TooltipLabel } from 'components/TooltipLabel'
import { QuestionBuilderConsumer } from 'organisms/QuestionBuilderCard'
import { FieldArray } from 'formik'
import { questionMap } from 'domains/survey'
import ChoiceQuestionInput from 'components/ChoiceQuestionInput'
import { QuestionType } from 'packages/question-types'

const Dropdown = styled(UnstyledDropdown)`
  > div.text {
    display: inline-flex !important;
    align-items: center;
  }
`

const LabelInDropdown = styled.span`
  font-size: 1.125em;
  line-height: 1em;
`

const DropdownWrapper = styled(Dropdown)`
  &.ui.selection.dropdown.disabled {
    background: rgba(244, 244, 244, 0.8);
    color: #373f46;
    opacity: 1;
  }
`

export const QuestionTypeField = ({ type }) => {
  // Metric questions can carry rating types (`star`, `rating_scale_minmax`, …)
  // that aren't editable choices in `questionMap`. Default to multipleChoice so
  // the field still renders instead of crashing on an undefined map entry.
  const { label, iconClassName } = questionMap[type] || questionMap.multipleChoice

  return (
    <Form.Field>
      <label>Question type</label>
      <DropdownWrapper
        text={
          <React.Fragment>
            <i className={classNames('icon ', iconClassName)} style={{ fontSize: '1.2em' }} />
            <LabelInDropdown>{label}</LabelInDropdown>
          </React.Fragment>
        }
        icon=""
        selection
        disabled
        options={[]}
      />
    </Form.Field>
  )
}

export const QuestionInput = () => {
  return (
    <QuestionBuilderConsumer>
      {({ question, questionIndex, handleFieldChange }) => {
        return (
          <React.Fragment>
            <QuestionTypeField type={question.type} />
            <Form.Field>
              <TooltipLabel label="Question" />
              <Input
                className="large"
                required
                value={question.text}
                onChange={handleFieldChange('text')}
                data-testid="question-title"
              />
            </Form.Field>
            <QuestionSettings
              question={question}
              questionIndex={questionIndex}
              handleFieldChange={handleFieldChange}
            />
            <Form.Field>
              <Checkbox
                label="Make this question required"
                checked={question.isRequired}
                onChange={handleFieldChange('isRequired', (_, { checked }) => {
                  return checked
                })}
                data-testid="question-required-toggle"
              />
              <span>
                <TooltipLabel
                  descriptionTitle="Required question"
                  descriptionContent="Required questions cannot be skipped. This will ensure questions never contain empty answers but might reduce overall survey response rates."
                  learnMoreLink="https://help.simplesat.io/en/articles/2776705-survey-questions#required-questions"
                />
              </span>
            </Form.Field>
          </React.Fragment>
        )
      }}
    </QuestionBuilderConsumer>
  )
}

function QuestionSettings({ question, questionIndex, handleFieldChange }) {
  if (question.type === QuestionType.SLIDER) {
    return <SliderQuestionInput />
  }

  if (question.type === QuestionType.MULTIPLE_CHOICE) {
    return (
      <FieldArray name={`questions.${questionIndex}.choices`}>
        {(arrayHelpers) => (
          <MultipleChoiceQuestionInput
            key={question.id || question.uniqueIdentifier}
            arrayHelpers={arrayHelpers}
            handleFieldChange={handleFieldChange}
          />
        )}
      </FieldArray>
    )
  }

  if (question.type === QuestionType.MULTI_SELECT_CHOICE) {
    return (
      <FieldArray name={`questions.${questionIndex}.choices`}>
        {(arrayHelpers) => (
          <ChoiceQuestionInput
            key={question.id || question.uniqueIdentifier}
            choices={question.choices}
            deletedExistChoices={R.prop('deletedExistChoices', question) || []}
            arrayHelpers={arrayHelpers}
            handleFieldChange={handleFieldChange}
          />
        )}
      </FieldArray>
    )
  }

  return null
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/QuestionInput/index.js